<script setup lang="ts">
import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { useDisplayName } from '@/composables/useDisplayName';
import KeyboardNavigableList from '@/core/elements/KeyboardNavigableList.vue';
import StudentListAddAction from '@/core/students/StudentListAddAction.vue';
import StudentListOverviewItem from '@/core/students/StudentListOverviewItem.vue';
import { computed } from 'vue';
import type { IPerformanceAssessmentEntryStudentView } from './performanceAssessmentEntryDetailDialog.types';
import PerformanceAssessmentStudentListItem from './PerformanceAssessmentStudentListItem.vue';

const props = defineProps<{
  gradeConfiguration: ISchoolGradeDisplayConfiguration | null;
  hasPerformanceVisual: boolean;
  overviewLabel: string;
  selectedStudentId: number | null;
  showAddStudents?: boolean;
  studentViews: IPerformanceAssessmentEntryStudentView[];
}>();

const emit = defineEmits<{
  'request-add': [anchor: HTMLElement];
  select: [studentId: number | null];
}>();

const { getStudentDisplayName } = useDisplayName();
const items = computed(() => (props.studentViews.length > 1 ? [null, ...props.studentViews] : props.studentViews));
const getStudentKey = (studentView: IPerformanceAssessmentEntryStudentView | null): number | string =>
  studentView?.student.id ?? 'all';
const getStudentValue = (studentView: IPerformanceAssessmentEntryStudentView | null): number | null =>
  studentView?.student.id ?? null;
</script>

<template>
  <KeyboardNavigableList
    :get-key="getStudentKey"
    :get-value="getStudentValue"
    :items="items"
    :model-value="selectedStudentId"
    @update:model-value="emit('select', $event)"
  >
    <template #default="{ item: studentView, itemAttributes, select, selected }">
      <StudentListOverviewItem
        v-if="!studentView"
        v-bind="itemAttributes"
        :label="overviewLabel"
        :selected="selected"
        @select="select"
      />
      <PerformanceAssessmentStudentListItem
        v-else
        :grade-configuration="gradeConfiguration"
        v-bind="itemAttributes"
        :competence-ratings="studentView.competenceRatings"
        :fully-rated="studentView.fullyRated"
        :has-performance-visual="hasPerformanceVisual"
        :has-note="studentView.hasNote"
        :name="getStudentDisplayName(studentView.student)"
        :overall-rating="studentView.overallRating"
        :grade-value="studentView.gradeValue"
        :selected="selected"
        :student="studentView.student"
        @select="select"
      />
    </template>

    <template v-if="showAddStudents" #footer>
      <StudentListAddAction @request="emit('request-add', $event)" />
    </template>
  </KeyboardNavigableList>
</template>
